import format from "@app/lib/format";

export const GENERAL_SECTION = "general";

const METADATA_KEYS = new Set([
    "device_names",
    "manufacturer",
    "intended_purpose",
    "clinical_study_titles",
    "biocompatibility_tests",
    "non_clinical_tests",
    "equivalent_device_names",
    "equivalent_manufacturers",
    "launch_country",
    "date_format",
    "launch_date",
]);

const SECTION_KEYS = new Set([
    "sections_selected",
    "section_documents",
    "section_priority",
    "section_priority_enabled",
    "section_articles",
]);

const isEmpty = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");

const COMMA = ",";
const COMMA_LIST_JOINER = ", ";

export const commaListText = (value) =>
    Array.isArray(value) ? value.join(COMMA_LIST_JOINER) : value || "";

const COMPETITOR_SEPARATOR = ": ";

export const competitorEntry = (value) => {
    if (typeof value !== "string") {
        return { manufacturer: value?.manufacturer || "", device: value?.device || "" };
    }
    const split = value.lastIndexOf(COMPETITOR_SEPARATOR);
    if (split === -1) {
        return { manufacturer: "", device: value };
    }
    return {
        manufacturer: value.slice(split + COMPETITOR_SEPARATOR.length),
        device: value.slice(0, split),
    };
};

export const competitorEntries = (value) =>
    (Array.isArray(value) ? value : []).map(competitorEntry);

const competitorText = ({ manufacturer, device }) =>
    manufacturer ? `${device}${COMPETITOR_SEPARATOR}${manufacturer}` : device;

export const itemEntries = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    return isEmpty(value) ? [] : [value];
};

export const itemSource = (field, values) =>
    Array.isArray(values[field.perItemOf]) ? values[field.perItemOf] : [];

function alignedToSource(field, value, values) {
    const entries = itemEntries(value);
    const namedIndexes = itemSource(field, values)
        .map((name, index) => (trimmed(name) ? index : null))
        .filter((index) => index !== null);
    const indexes = namedIndexes.length ? namedIndexes : [0];
    const aligned = indexes.map((index) => trimmed(entries[index]));
    return aligned.some(Boolean) ? aligned : [];
}

function sanitize(field, value, values = {}) {
    if (field.perItemOf) {
        return alignedToSource(field, value, values);
    }

    if (field.type === "text_range") {
        const range = [trimmed(value?.[0]), trimmed(value?.[1])];
        return range.some(Boolean) ? range : [];
    }

    if (field.type === "string_list") {
        return (Array.isArray(value) ? value : [])
            .map((v) => (typeof v === "string" ? v.trim() : v))
            .filter((v) => v !== "" && v !== null && v !== undefined);
    }

    if (field.type === "comma_list") {
        return commaListText(value).split(COMMA).map(trimmed).filter(Boolean);
    }

    if (field.type === "clinical_entry_list") {
        return (Array.isArray(value) ? value : [])
            .map((entry) => {
                const out = { name: (entry?.name || "").trim() };
                if (entry?.device?.trim()) {
                    out.device = entry.device.trim();
                }
                if (entry?.source_document?.trim()) {
                    out.source_document = entry.source_document.trim();
                }
                return out;
            })
            .filter((entry) => entry.name);
    }

    if (field.type === "competitor_list") {
        return competitorEntries(value)
            .map(({ manufacturer, device }) => ({
                manufacturer: trimmed(manufacturer),
                device: trimmed(device),
            }))
            .filter((entry) => entry.device)
            .map(competitorText);
    }

    return value;
}

export const isRequired = (field, values) => {
    if (field.required) {
        return true;
    }
    if (field.requiredIf) {
        return values[field.requiredIf.field] === field.requiredIf.value;
    }
    return false;
};

export const isVisible = (field, values) => {
    if (field.visibleIf && values[field.visibleIf.field] !== field.visibleIf.value) {
        return false;
    }
    if (field.hiddenIf && values[field.hiddenIf.field] === field.hiddenIf.value) {
        return false;
    }
    return true;
};

const isBlank = (field, values) => {
    const cleaned = sanitize(field, values[field.name], values);
    if (field.everyItem) {
        return !cleaned.length || cleaned.some((entry) => !entry);
    }
    if (field.perItemOf) {
        return !cleaned[0];
    }
    return isEmpty(typeof cleaned === "string" ? cleaned.trim() : cleaned);
};

export function missingRequired(fields, values) {
    return fields
        .filter((field) => isVisible(field, values) && isRequired(field, values))
        .filter((field) => isBlank(field, values))
        .map((field) => field.name);
}

const isIncompleteCompetitor = ({ manufacturer, device }) =>
    Boolean(trimmed(manufacturer)) && !trimmed(device);

export const hasIncompleteEntries = (field, values) =>
    field.type === "competitor_list" &&
    competitorEntries(values[field.name]).some(isIncompleteCompetitor);

export function incompleteFields(fields, values) {
    return fields
        .filter((field) => isVisible(field, values))
        .filter((field) => hasIncompleteEntries(field, values))
        .map((field) => field.name);
}

export function fieldBlocks(template, section) {
    const blocks = [];
    for (const field of template.fields) {
        if (field.section !== section) {
            continue;
        }
        const name = field.block || "";
        const last = blocks[blocks.length - 1];
        if (last && last.name === name) {
            last.fields.push(field);
        } else {
            blocks.push({ id: `${section}:${name}`, name, fields: [field] });
        }
    }
    return blocks;
}

export function missingItemLabel(template, item) {
    const field = template.fields.find((f) => !f.group && f.name === item.field);
    return field ? field.label : item.label;
}

const toUTC = (field, value) => {
    if (field.type === "date") {
        return format.toUTC(value).toISOString();
    }

    return value;
};

const toLocal = (field, value) => {
    if (field.type === "date") {
        return format.toLocal(value).toISOString();
    }

    return value;
};

const selectedSections = (formValues, template) =>
    template.fixedSections ||
    (Array.isArray(formValues.sections_selected) ? formValues.sections_selected : []);

export function toPayload(formValues, knownDocumentIds = null, knownArticleIds = null, template) {
    const metadata = {};
    const extraMetadata = {};
    const tables = Object.fromEntries((template.payloadTables || []).map((t) => [t, {}]));

    for (const field of template.fields) {
        if (SECTION_KEYS.has(field.name) || template.nonMetadataKeys.has(field.name)) {
            continue;
        }

        const value = sanitize(field, formValues[field.name], formValues);
        if (isEmpty(value)) {
            continue;
        }

        const serialized = toUTC(field, value);

        if (field.group) {
            tables[field.group][field.wireName || field.name] = serialized;
        } else if (METADATA_KEYS.has(field.name)) {
            metadata[field.name] = serialized;
        } else {
            extraMetadata[field.name] = serialized;
        }
    }

    const selected = selectedSections(formValues, template);
    const known = knownDocumentIds ? new Set(knownDocumentIds) : null;
    const knownArticles = knownArticleIds ? new Set(knownArticleIds) : null;
    const rawDocuments = formValues.section_documents || {};
    const rawPriority = formValues.section_priority || {};
    const priorityEnabled = formValues.section_priority_enabled || {};
    const rawArticles = formValues.section_articles || {};

    const section_documents = {};
    const section_priority = {};
    const section_articles = {};

    const configured = [
        ...new Set([
            ...selected,
            ...Object.keys(rawDocuments),
            ...Object.keys(rawArticles),
            ...Object.keys(rawPriority),
        ]),
    ];

    for (const key of configured) {
        if (template.sectionArticleType[key]) {
            section_articles[key] = (Array.isArray(rawArticles[key])
                ? rawArticles[key]
                : []
            ).filter((id) => !knownArticles || knownArticles.has(id));
            continue;
        }

        if (template.sectionsWithoutDocuments.has(key)) {
            section_documents[key] = [];
            continue;
        }

        const assigned = (Array.isArray(rawDocuments[key]) ? rawDocuments[key] : []).filter(
            (id) => !known || known.has(id),
        );
        section_documents[key] = assigned;

        if (priorityEnabled[key]) {
            const scope = new Set(assigned);
            const ranked = (Array.isArray(rawPriority[key]) ? rawPriority[key] : []).filter((id) =>
                scope.has(id),
            );
            if (ranked.length) {
                section_priority[key] = ranked;
            }
        }
    }

    const deviceFamilyName = trimmed(formValues.device_family_type);
    const device_family = deviceFamilyName ? [{ device_family_name: deviceFamilyName }] : [];

    return {
        metadata,
        extraMetadata,
        ...tables,
        ...(template.emitsDeviceFamily ? { device_family } : {}),
        sections_selected: selected,
        section_documents,
        section_priority,
        section_articles,
    };
}

export function fromConfig(config, template) {
    const fixed = template.fixedSections;

    if (!config) {
        return fixed
            ? {
                  sections_selected: [...fixed],
                  section_documents: {},
                  section_priority: {},
                  section_articles: {},
                  section_priority_enabled: {},
              }
            : {};
    }

    const values = {
        ...(config.metadata || {}),
        ...(config.extraMetadata || {}),
    };

    for (const field of template.fields) {
        if (field.group || field.type !== "date") {
            continue;
        }
        if (!isEmpty(values[field.name])) {
            values[field.name] = toLocal(field, values[field.name]);
        }
    }

    const devFamily = (config.device_family || [])[0];
    if (devFamily && !values.device_family_type) {
        values.device_family_type = devFamily.device_family_name;
    }

    for (const field of template.fields) {
        if (!field.group) {
            continue;
        }
        const table = config[field.group] || {};
        const stored = table[field.wireName || field.name];
        if (!isEmpty(stored)) {
            values[field.name] = stored;
        }
    }

    const sectionDocuments = config.section_documents || {};
    const sectionPriority = config.section_priority || {};
    const sectionArticles = config.section_articles || {};

    values.section_documents = { ...sectionDocuments };
    values.section_priority = { ...sectionPriority };
    values.section_articles = { ...sectionArticles };
    values.sections_selected = fixed ? [...fixed] : storedSelection(config);
    values.section_priority_enabled = Object.keys(sectionPriority).reduce((acc, key) => {
        acc[key] = true;
        return acc;
    }, {});

    return values;
}

export function mergeFromConfig(formValues, config, template, { sectionKey = null } = {}) {
    const incoming = fromConfig(config, template);
    const merged = { ...formValues };

    const fieldNames = template.fields
        .filter((field) => !SECTION_KEYS.has(field.name))
        .filter((field) => !sectionKey || field.section === sectionKey)
        .map((field) => field.name);

    for (const name of fieldNames) {
        if (!isEmpty(incoming[name])) {
            merged[name] = incoming[name];
        }
    }

    const sectionMaps = ["section_documents", "section_priority", "section_articles"];
    const copiedSections = sectionKey ? [sectionKey] : storedSelection(config);

    for (const map of sectionMaps) {
        const next = { ...(merged[map] || {}) };
        for (const key of copiedSections) {
            if (incoming[map]?.[key] !== undefined) {
                next[key] = incoming[map][key];
            }
        }
        merged[map] = next;
    }

    merged.section_priority_enabled = Object.keys(merged.section_priority || {}).reduce(
        (acc, key) => {
            acc[key] = true;
            return acc;
        },
        {},
    );

    const selected = Array.isArray(merged.sections_selected) ? merged.sections_selected : [];
    merged.sections_selected = template.fixedSections
        ? [...template.fixedSections]
        : [...new Set([...selected, ...copiedSections])];

    return merged;
}

export function storedSelection(config) {
    if (Array.isArray(config?.sections_selected)) {
        return [...config.sections_selected];
    }
    return [
        ...new Set([
            ...Object.keys(config?.section_documents || {}),
            ...Object.keys(config?.section_articles || {}),
        ]),
    ];
}

export function hasSectionData(config, template, sectionKey) {
    const maps = ["section_documents", "section_priority", "section_articles"];
    if (maps.some((map) => (config?.[map]?.[sectionKey] || []).length)) {
        return true;
    }

    const values = fromConfig(config, template);
    return template.fields
        .filter((field) => field.section === sectionKey)
        .some((field) => !isEmpty(values[field.name]));
}
